import { useState } from "react";
import Modal from "@/components/Modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import {
  useGetReceiptQuery,
  useSendReceiptMutation,
  useLazyGetReceiptPdfQuery,
} from "@/redux/services/pos/receipt.api";
import {
  useCreateCustomerMutation,
  useGetRestaurantCustomersQuery,
} from "@/redux/services/customer.api";
import { RestaurantCustomer } from "@/redux/services/customer.type";
import { useAppSelector } from "@/redux/features/hooks";
import { useDebounce } from "@/hooks/useDebounce";
import { useRestaurantCurrency } from "@/lib/hooks";
import {
  displayPrice,
  getPaymentMethodLabel,
  getOrderTypeLabel,
  formatDate,
  formatPhoneNumber,
} from "@/lib/utils";
import { Download, Loader2, Printer, Send } from "lucide-react";
import { OrderType } from "@/redux/services/order/order.type";
import {
  printOrderReceipt,
  PrintBridgeError,
} from "@/services/PrintBridgeService";
import toast from "react-hot-toast";

interface ReceiptPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
}

const ReceiptPreview: React.FC<ReceiptPreviewProps> = ({
  isOpen,
  onClose,
  orderId,
}) => {
  const currency = useRestaurantCurrency();
  const {
    data: receipt,
    isLoading,
    isError,
  } = useGetReceiptQuery({ orderUuid: orderId }, { skip: !isOpen });

  const [sendReceipt, { isLoading: isSending }] = useSendReceiptMutation();
  const [fetchPdf, { isFetching: isPdfLoading }] = useLazyGetReceiptPdfQuery();
  const [createCustomer, { isLoading: isCreatingCustomer }] =
    useCreateCustomerMutation();

  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [hasPickedCustomer, setHasPickedCustomer] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const restaurantId = useAppSelector(
    (state) => state.user.currentRestaurant?.id
  );
  const debouncedPhone = useDebounce(phone.trim(), 400);
  const shouldSearch =
    showCustomerForm &&
    !hasPickedCustomer &&
    debouncedPhone.length >= 3 &&
    !!restaurantId;

  const { data: matches, isFetching: isSearching } =
    useGetRestaurantCustomersQuery(
      {
        restaurantId: restaurantId ?? "",
        "customer.phone": debouncedPhone,
        limit: 5,
      },
      { skip: !shouldSearch }
    );

  const suggestions = shouldSearch ? (matches?.results ?? []) : [];

  const handleSelectCustomer = (match: RestaurantCustomer) => {
    setPhone(match.customer.phone);
    setFullName(match.customer.fullName ?? "");
    setHasPickedCustomer(true);
  };

  const hasCustomer =
    receipt?.customer &&
    (typeof receipt.customer === "string"
      ? !!receipt.customer
      : !!(receipt.customer.name ?? receipt.customer.phone));

  const isPaid = (receipt?.remainingAmount ?? 0) <= 0;
  const documentLabel = isPaid ? "Reçu" : "Addition";
  const fileSlug = isPaid ? "recu" : "addition";

  const handlePrint = async () => {
    setIsPrinting(true);
    try {
      await printOrderReceipt(orderId);
      toast.success("Reçu imprimé");
    } catch (e) {
      const message =
        e instanceof PrintBridgeError && e.stage === "send-to-bridge"
          ? "Imprimante locale injoignable. Vérifiez le service d'impression sur la caisse."
          : "Erreur lors de l'impression du reçu";
      toast.error(message);
    } finally {
      setIsPrinting(false);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!hasCustomer) {
      setShowCustomerForm(true);
      return;
    }

    try {
      await sendReceipt({ orderUuid: orderId }).unwrap();
      toast.success("Reçu envoyé par WhatsApp");
    } catch {
      toast.error("Erreur lors de l'envoi du reçu");
    }
  };

  const handleCreateAndSend = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fullName.trim()) {
      toast.error("Le nom du client est requis");
      return;
    }
    if (!phone.trim()) {
      toast.error("Le numéro WhatsApp est requis");
      return;
    }

    try {
      await createCustomer({
        fullName: fullName.trim(),
        phone: phone.trim(),
        orderId,
      }).unwrap();

      await sendReceipt({ orderUuid: orderId }).unwrap();
      toast.success("Reçu envoyé par WhatsApp");
      setShowCustomerForm(false);
      setFullName("");
      setPhone("");
      setHasPickedCustomer(false);
    } catch {
      toast.error("Erreur lors de l'envoi du reçu");
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const result = await fetchPdf({ orderUuid: orderId }).unwrap();
      const blob = new Blob([result], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileSlug}-${receipt?.orderNumber ?? orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors du téléchargement du reçu");
    }
  };

  const handleClose = () => {
    setShowCustomerForm(false);
    setFullName("");
    setPhone("");
    setHasPickedCustomer(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={documentLabel}
      size="small"
    >
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {isError && (
        <div className="py-8 text-center text-sm text-red-500">
          Erreur lors du chargement du reçu.
        </div>
      )}

      {receipt && (
        <div className="font-mono text-sm">
          {/* Payment status banner */}
          <div
            className={
              isPaid
                ? "mb-3 rounded-md border border-green-500/30 bg-green-500/10 px-3 py-1.5 text-center text-xs font-semibold uppercase tracking-wide text-green-700"
                : "mb-3 rounded-md border border-danger/30 bg-danger/10 px-3 py-1.5 text-center text-xs font-semibold uppercase tracking-wide text-danger"
            }
          >
            {isPaid ? "Payé" : "Non payé"}
          </div>

          {/* Restaurant header */}
          <div className="text-center">
            <p className="text-base font-bold">
              {typeof receipt.restaurant === "object"
                ? receipt.restaurant.name
                : receipt.restaurant}
            </p>
            {typeof receipt.restaurant === "object" &&
              receipt.restaurant.address && (
                <p className="text-xs text-muted-foreground">
                  {receipt.restaurant.address}
                </p>
              )}
          </div>

          {/* Divider */}
          <div className="my-3 border-t border-dashed border-border" />

          {/* Order info */}
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Commande</span>
              <span className="font-medium">#{receipt.orderNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Date</span>
              <span>{formatDate(new Date(receipt.date), true, true)}</span>
            </div>
            {receipt.table && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Table</span>
                <span>{receipt.table}</span>
              </div>
            )}
            {receipt.orderType && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Type</span>
                <span>{getOrderTypeLabel(receipt.orderType as OrderType)}</span>
              </div>
            )}
            {receipt.deliveryAddress && (
              <div className="flex justify-between gap-4">
                <span className="whitespace-nowrap text-muted-foreground">
                  Adresse de livraison
                </span>
                <span className="text-right">{receipt.deliveryAddress}</span>
              </div>
            )}
            {hasCustomer && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Client</span>
                <span>
                  {typeof receipt.customer === "object"
                    ? (receipt.customer?.name ?? receipt.customer?.phone ?? "")
                    : receipt.customer}
                </span>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="my-3 border-t border-dashed border-border" />

          {/* Items */}
          <div className="space-y-2">
            {receipt.items.map((item, index) => (
              <div key={index}>
                <div className="flex justify-between">
                  <span className="flex-1">
                    {item.quantity}x {item.name}
                  </span>
                  <span className="ml-4 whitespace-nowrap text-right">
                    {displayPrice(item.totalPrice, currency)}
                  </span>
                </div>
                {item.quantity > 1 && (
                  <div className="text-xs text-muted-foreground">
                    @ {displayPrice(item.unitPrice, currency)}
                  </div>
                )}
                {item.selectedOptions && item.selectedOptions.length > 0 && (
                  <div className="pl-3 text-xs text-muted-foreground space-y-0.5">
                    {item.selectedOptions.map((opt, i) => (
                      <div key={i} className="flex justify-between">
                        <span>
                          {opt.showTitle ? `${opt.optionTitle}: ` : ""}
                          {opt.value}
                        </span>
                        {opt.priceModifier !== 0 && (
                          <span className="ml-2 whitespace-nowrap">
                            +{displayPrice(opt.priceModifier, currency)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {item.selections && item.selections.length > 0 && (
                  <div className="pl-3 text-xs text-muted-foreground space-y-0.5">
                    {item.selections.map((sel, i) => (
                      <div key={i}>
                        <div className="flex justify-between">
                          <span>- {sel.name}</span>
                          {sel.supplement != null && sel.supplement > 0 && (
                            <span className="ml-2 whitespace-nowrap">
                              +{displayPrice(sel.supplement, currency)}
                            </span>
                          )}
                        </div>
                        {sel.selectedOptions?.map((opt, j) => (
                          <div key={j} className="flex justify-between pl-3">
                            <span>
                              {opt.showTitle ? `${opt.optionTitle}: ` : ""}
                              {opt.value}
                            </span>
                            {opt.priceModifier !== 0 && (
                              <span className="ml-2 whitespace-nowrap">
                                +{displayPrice(opt.priceModifier, currency)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {item.additionalDemands && (
                  <div className="text-xs text-muted-foreground italic">
                    {item.additionalDemands}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="my-3 border-t border-dashed border-border" />

          {/* Totals */}
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Sous-total</span>
              <span>{displayPrice(receipt.subtotal, currency)}</span>
            </div>
            {(receipt.deliveryFee ?? 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Frais de livraison
                </span>
                <span>
                  {displayPrice(receipt.deliveryFee ?? 0, currency)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold">
              <span>Total</span>
              <span>{displayPrice(receipt.totalPrice, currency)}</span>
            </div>
          </div>

          {/* Payments */}
          {receipt.payments.length > 0 && (
            <>
              <div className="my-3 border-t border-dashed border-border" />
              <div className="space-y-1">
                {receipt.payments.map((payment, index) => (
                  <div key={index} className="flex justify-between">
                    <span className="text-muted-foreground">
                      {getPaymentMethodLabel(payment.method)}
                      {payment.method === "mobile money" &&
                        payment.metadata?.provider &&
                        ` (${payment.metadata.provider})`}
                    </span>
                    <span>{displayPrice(payment.amount, currency)}</span>
                  </div>
                ))}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payé</span>
                  <span>{displayPrice(receipt.paidAmount, currency)}</span>
                </div>
                {receipt.remainingAmount > 0 && (
                  <div className="flex justify-between font-medium text-red-500">
                    <span>Restant</span>
                    <span>
                      {displayPrice(receipt.remainingAmount, currency)}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Footer */}
          <div className="my-3 border-t border-dashed border-border" />
          <p className="text-center text-muted-foreground">
            {isPaid
              ? "Merci de votre visite !"
              : "Document non libératoire — en attente de paiement"}
          </p>

          {/* Customer form (shown when no customer) */}
          {showCustomerForm && (
            <>
              <div className="my-3 border-t border-dashed border-border" />
              <form
                onSubmit={handleCreateAndSend}
                className="space-y-3 font-sans"
              >
                <p className="text-sm text-muted-foreground">
                  Renseignez le client pour envoyer le reçu
                </p>
                <div className="relative">
                  <Input
                    label="Numéro WhatsApp"
                    type="tel"
                    id="customer-phone"
                    placeholder="+224 6XX XX XX XX"
                    required
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      if (hasPickedCustomer) setHasPickedCustomer(false);
                    }}
                  />
                  {shouldSearch && !isSearching && suggestions.length > 0 && (
                    <ul className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-md">
                      {suggestions.map((match) => (
                        <li key={match.id}>
                          <button
                            type="button"
                            onClick={() => handleSelectCustomer(match)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex flex-col"
                          >
                            <span className="font-medium text-dark">
                              {match.customer.fullName || "Sans nom"}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatPhoneNumber(match.customer.phone)}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <Input
                    label="Nom du client"
                    type="text"
                    id="customer-name"
                    placeholder="Nom complet"
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCustomerForm(false)}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    isLoading={isCreatingCustomer || isSending}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Envoyer
                  </Button>
                </div>
              </form>
            </>
          )}

          {/* Action buttons */}
          {!showCustomerForm && (
            <div className="mt-6 flex flex-wrap justify-center gap-2 print:hidden">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                disabled={isPrinting}
              >
                {isPrinting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Printer className="mr-2 h-4 w-4" />
                )}
                Imprimer
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPdf}
                disabled={isPdfLoading}
              >
                {isPdfLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Télécharger
              </Button>
              <Button
                size="sm"
                onClick={handleSendWhatsApp}
                disabled={isSending}
              >
                {isSending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                WhatsApp
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ReceiptPreview;
